import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { LinkButtonAdapter } from "../link-button-adapter/LinkButtonAdapter";
import { DoubleImage as ImageBlock } from "../../models";
import {
  BlockHeader,
  HeaderLevel,
} from "../../../common/block-header/BlockHeader";
import { BlockBody } from "../../../common/block-body/BlockBody";

type Props = {
  leftBlock: ImageBlock[];
  rightBlock: ImageBlock[];
};

export const DoubleImage: FC<Props> = ({ leftBlock, rightBlock }) => (
  <div
    className={classNames(
      "mt-20 flex flex-col gap-20 px-5",
      "md:flex-row md:gap-10",
      "lg:mt-25 lg:gap-15 lg:px-16",
      "xl:mt-40 xl:gap-12 xl:px-24",
      "xl:max-w-container-wide",
      "xl:mx-auto",
    )}
  >
    {[leftBlock[0], rightBlock[0]].map((imageBlock) => (
      <div
        className={classNames(
          "rounded-lg border border-solid border-divider",
          "md:basis-1/2 md:self-start md:self-stretch",
        )}
        key={imageBlock._uid}
      >
        <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
          <GatsbyImage
            image={getGatsbyImage(imageBlock.image.filename, {
              layout: "constrained",
            })}
            alt={imageBlock.image.alt}
            className={classNames(
              "aspect-[4/3] w-full rounded-t-lg",
              "lg:aspect-[9/6]",
              "xl:aspect-[16/9]",
            )}
          />
          <div
            className={classNames("px-8 py-5", "lg:px-10", "xl:px-15 xl:py-13")}
          >
            <div
              className={classNames(
                "text-medium text-primary-content",
                "lg:text-medium",
                "xl:text-large",
                { "mb-2.5": imageBlock.text },
              )}
            >
              <BlockHeader
                richText={imageBlock.title}
                headerLevel={HeaderLevel.H4}
              />
            </div>
            <div
              className={classNames(
                "text-regular text-base-400",
                "[&>p]:mb-2.5 last:[&>p]:mb-0 [&>p]:lg:mb-5",
                { "mb-5 lg:mb-8": imageBlock.button?.length },
              )}
            >
              <BlockBody richText={imageBlock.text} />
            </div>
            {imageBlock.button?.length ? (
              <LinkButtonAdapter data={imageBlock.button[0]} />
            ) : null}
          </div>
        </Fade>
      </div>
    ))}
  </div>
);
